import React, { useState } from "react";
import { personalInfo } from "../data/mock";
import { ArrowDown, X, Mail, User, Phone, Download } from "lucide-react";
import { FEATURES } from "../config/features";
import { API_BASE_URL } from "../config/api";

const Hero = () => {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadReady, setDownloadReady] = useState(false);
  const API_URL = API_BASE_URL;

  const scrollToAbout = () => {
    const aboutSection = document.getElementById("about");
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-()]+$/.test(formData.phone)) {
      errors.phone = 'Phone number is invalid';
    }
    
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/download/request-epiic-hero`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        })
      });
      
      if (response.ok) {
        setDownloadReady(true);
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      alert('Connection error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Video Background Layer - Full Screen */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectFit: 'cover' }}
        >
          <source src="https://customer-assets.emergentagent.com/job_ct-digital-hub/artifacts/zxwbwppj_KOTF%20-%20Seeds.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* Video Overlay for Better Text Contrast */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-purple-900/50 to-black/70" />
        
        {/* Additional vignette for text readability */}
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/60" />
      </div>

      {/* Subtle animated overlay effects */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Sacred geometry accents */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 border-2 border-yellow-400 rotate-45 animate-spin" style={{animationDuration: '20s'}} />
          <div className="absolute top-20 right-20 w-24 h-24 border border-orange-400 rounded-full animate-pulse" style={{animationDuration: '3s'}} />
          <div className="absolute bottom-32 left-20 w-40 h-40 border border-purple-400" style={{transform: 'rotate(45deg)', animationDuration: '15s'}} />
        </div>
        
        {/* Fire particles */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-orange-600/30 via-red-500/10 to-transparent">
          <div className="absolute bottom-0 left-1/4 w-2 h-8 bg-orange-400 rounded-full animate-bounce" style={{animationDelay: '0s'}} />
          <div className="absolute bottom-0 left-1/2 w-3 h-12 bg-yellow-400 rounded-full animate-bounce" style={{animationDelay: '0.5s'}} />
          <div className="absolute bottom-0 right-1/3 w-2 h-6 bg-red-400 rounded-full animate-bounce" style={{animationDelay: '1s'}} />
        </div>
      </div>

      {/* Sacred Symbols */}
      <div className="absolute inset-0 overflow-hidden">
        {/* 888 Numerology */}
        <div className="absolute top-16 right-16 text-6xl font-light text-yellow-400/20 animate-pulse" style={{animationDuration: '3s'}}>
          888
        </div>
        
        {/* All-seeing eye */}
        <div className="absolute bottom-20 left-16 w-16 h-16 border-2 border-yellow-400/30 rounded-full flex items-center justify-center animate-spin" style={{animationDuration: '25s'}}>
          <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full opacity-60" />
        </div>
        
        {/* Chakra colors flowing */}
        <div className="absolute top-1/3 left-8 flex flex-col space-y-2">
          <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse" style={{animationDelay: '0s'}} />
          <div className="w-4 h-4 bg-orange-500 rounded-full animate-pulse" style={{animationDelay: '0.5s'}} />
          <div className="w-4 h-4 bg-yellow-500 rounded-full animate-pulse" style={{animationDelay: '1s'}} />
          <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse" style={{animationDelay: '1.5s'}} />
          <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse" style={{animationDelay: '2s'}} />
          <div className="w-4 h-4 bg-indigo-500 rounded-full animate-pulse" style={{animationDelay: '2.5s'}} />
          <div className="w-4 h-4 bg-purple-500 rounded-full animate-pulse" style={{animationDelay: '3s'}} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 items-center min-h-[80vh]">
          {/* Left Column - Main Content */}
          <div className="space-y-8 animate-fade-in text-left">
            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-6xl lg:text-8xl font-extralight tracking-tighter leading-none relative">
                <span className="bg-gradient-to-r from-yellow-300 via-orange-400 to-red-500 bg-clip-text text-transparent animate-pulse drop-shadow-2xl">
                  {personalInfo.name}
                </span>
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400/20 via-orange-500/20 to-red-500/20 blur-xl -z-10 animate-pulse" />
              </h1>
              <h2 className="text-2xl lg:text-3xl font-light tracking-wide">
                <span 
                  className="bg-gradient-to-r from-purple-300 via-orange-300 to-yellow-300 bg-clip-text text-transparent"
                  style={{
                    textShadow: '0 0 30px rgba(0,0,0,0.8), 0 2px 6px rgba(0,0,0,0.6), 0 0 60px rgba(168,85,247,0.2)'
                  }}
                >
                  {personalInfo.title}
                </span>
              </h2>
            </div>

            {/* Tagline */}
            <div className="inline-block">
              <div className="bg-black/20 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/5 shadow-md">
                <p className="text-base lg:text-lg font-light text-white leading-relaxed tracking-wide">
                  {personalInfo.tagline}
                </p>
              </div>
            </div>

            {/* Bridge Philosophy - Creator/Creator Echo */}
            <div className="inline-block">
              <div className="bg-gradient-to-r from-purple-900/40 to-orange-900/40 backdrop-blur-sm rounded-lg px-5 py-3 border border-yellow-400/20 shadow-lg">
                <p className="text-sm lg:text-base font-light leading-relaxed tracking-wide">
                  <span className="bg-gradient-to-r from-teal-300 via-purple-300 to-amber-300 bg-clip-text text-transparent italic">
                    "{personalInfo.bridge}"
                  </span>
                </p>
              </div>
            </div>

            {/* Philosophy & Motto */}
            <div className="space-y-4 pt-4">
              <div className="text-sm uppercase tracking-widest font-normal">
                <span 
                  className="bg-gradient-to-r from-cyan-300 to-teal-400 bg-clip-text text-transparent animate-pulse"
                  style={{
                    textShadow: '0 0 25px rgba(0,0,0,0.8), 0 2px 5px rgba(0,0,0,0.7), 0 0 50px rgba(34,211,238,0.2)'
                  }}
                >
                  {personalInfo.philosophy}
                </span>
              </div>
              <div className="inline-block">
                <div className="bg-black/20 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/5 shadow-md">
                  <div className="text-xl font-light tracking-wide relative">
                    <span 
                      className="bg-gradient-to-r from-yellow-300 via-orange-400 to-red-400 bg-clip-text text-transparent"
                      style={{
                        textShadow: '0 0 30px rgba(0,0,0,0.9), 0 2px 6px rgba(0,0,0,0.7)'
                      }}
                    >
                      {personalInfo.motto}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scroll Indicator */}
            <div className="pt-8">
              <button
                onClick={scrollToAbout}
                className="group flex flex-col items-start space-y-2 text-orange-300 hover:text-yellow-300 transition-colors duration-300"
              >
                <span className="text-xs uppercase tracking-widest font-normal">
                  Discover More
                </span>
                <ArrowDown 
                  size={20} 
                  className="animate-bounce group-hover:translate-y-1 transition-transform duration-300" 
                />
              </button>
            </div>
          </div>

          {/* Right Column - EPIIC HERO Call-to-Action (Feature Gated) */}
          {FEATURES.FREE_DOWNLOAD && (
            <div className="flex justify-end">
              <div className="bg-gradient-to-r from-black/80 via-purple-900/80 to-orange-900/80 backdrop-blur-sm border-2 border-yellow-400/30 rounded-2xl p-8 max-w-md shadow-2xl">
                <div className="text-center space-y-4">
                  <div className="text-yellow-400 text-sm uppercase tracking-widest font-light animate-pulse">
                    Ready to Begin Your
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-light bg-gradient-to-r from-yellow-300 via-orange-400 to-red-400 bg-clip-text text-transparent">
                    EPIIC HERO Journey?
                  </h3>
                  <p className="text-orange-200 text-sm font-light leading-relaxed">
                    FREE Download: "Comedy Hour: A Nation's Prayer — Fiyah Edition" - A 25-page visual experience from the World of INSPIYAH
                  </p>
                  <button 
                    onClick={() => setShowModal(true)}
                    className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-black px-6 py-3 rounded-full font-medium tracking-wide hover:scale-105 transition-transform duration-300 shadow-lg text-sm"
                    data-testid="epiic-hero-cta-btn"
                  >
                    Claim Your Free Download
                  </button>
                  <div className="text-xs text-purple-300 italic">
                    Join the World of INSPIYAH
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* EPIIC HERO Kit Popup Modal (Feature Gated) */}
      {FEATURES.EPIIC_HERO_MODAL && showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="relative bg-gradient-to-br from-gray-900 via-purple-900 to-orange-900 rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl border-2 border-yellow-400/50">
            {/* Close Button */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-block p-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mb-4">
                <Download size={32} className="text-black" />
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-yellow-300 via-orange-400 to-red-400 bg-clip-text text-transparent mb-2">
                Comedy Hour: A Nation's Prayer
              </h2>
              <p className="text-gray-300 text-sm">
                Enter your details to receive your free 25-page Fiyah Edition
              </p>
            </div>

            {/* Form */}
            {/* Form or Success State */}
            {downloadReady ? (
              <div className="text-center space-y-4 py-4">
                <div className="inline-block p-3 bg-green-500/20 rounded-full mb-2">
                  <Mail size={32} className="text-green-400" />
                </div>
                <h3 className="text-xl font-light text-white">Check Your Email!</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  We've sent your download link to <strong className="text-yellow-400">{formData.email}</strong>
                </p>
                <p className="text-gray-400 text-xs">
                  The link expires in 48 hours. Check your spam folder if you don't see it.
                </p>
                <button
                  onClick={() => { setShowModal(false); setDownloadReady(false); setFormData({ name: '', email: '', phone: '' }); }}
                  className="block mx-auto mt-4 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-black px-6 py-2 rounded-full font-medium tracking-wide hover:scale-105 transition-transform duration-300 shadow-lg text-sm"
                  data-testid="epiic-hero-close-btn"
                >
                  Close
                </button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className={`w-full pl-10 pr-4 py-3 bg-black/50 border ${
                      formErrors.name ? 'border-red-500' : 'border-gray-600'
                    } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all`}
                  />
                </div>
                {formErrors.name && (
                  <p className="text-red-400 text-xs mt-1">{formErrors.name}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    className={`w-full pl-10 pr-4 py-3 bg-black/50 border ${
                      formErrors.email ? 'border-red-500' : 'border-gray-600'
                    } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all`}
                  />
                </div>
                {formErrors.email && (
                  <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    className={`w-full pl-10 pr-4 py-3 bg-black/50 border ${
                      formErrors.phone ? 'border-red-500' : 'border-gray-600'
                    } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all`}
                  />
                </div>
                {formErrors.phone && (
                  <p className="text-red-400 text-xs mt-1">{formErrors.phone}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="epiic-hero-submit-btn"
                className={`w-full py-4 rounded-full font-semibold text-lg transition-all duration-300 ${
                  isSubmitting
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-black hover:scale-105 hover:shadow-2xl'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
                    <span>Processing...</span>
                  </span>
                ) : (
                  'Download My Free Copy'
                )}
              </button>

              <p className="text-xs text-gray-400 text-center mt-4">
                By downloading, you agree to receive occasional updates from the World of INSPIYAH. 
                You can unsubscribe anytime.
              </p>
            </form>
            )}
          </div>
        </div>
      )}

      {/* Custom CSS for fade-in animation */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
      `}</style>
    </section>
  );
};

export default Hero;